import { Button, Card, Group, Stack, Title } from '@mantine/core';
import { Plus, FileText } from 'react-feather';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import type { SessionAgendaSection } from '../../../../../services/api/sessionAgenda';
import { EmptyState } from '../../../../../components/common/EmptyState';
import { AgendaSectionCard } from './AgendaSectionCard';

/**
 * MED-975 (E1.3d) — Agenda Sections panel. Hosts the section list, the
 * empty state, the "+ Add Section" affordance, and reorder semantics.
 *
 * Reorder mirrors the Task Templates `PhaseAccordion` up/down pattern
 * referenced by the ticket's Technical Notes. The panel rewrites sort_order
 * on every mutation so the local state stays gap-free; the save API path
 * uses the array order as authoritative (server rewrites sort_order too).
 */

export interface AgendaSectionsPanelProps {
  sections: SessionAgendaSection[];
  hasSlideDeck: boolean;
  slideDeckDoc: PDFDocumentProxy | null;
  /**
   * MED-980 (E1.3e / AC-SCF2, AC-SLC2). When false, the per-section
   * commitment toggles inside each AgendaSectionCard are hidden entirely.
   */
  cohortCommitmentTrackingEnabled: boolean;
  onChange: (next: SessionAgendaSection[]) => void;
}

function emptySection(sortOrder: number): SessionAgendaSection {
  return {
    section_name: null,
    duration_minutes: null,
    sort_order: sortOrder,
    // MED-980 (E1.3e) — participant-facing fields start empty / off.
    // Defaults match the DB defaults (v212) so a new section round-trips
    // through a no-op Save cleanly.
    participant_prompt: null,
    show_commitment_field: false,
    show_last_session_commitment: false,
    visuals: [],
    leader_prompts: [],
  };
}

/** Renumber sort_order so the array stays gap-free 1..N. */
function renumber(sections: SessionAgendaSection[]): SessionAgendaSection[] {
  return sections.map((s, i) => ({ ...s, sort_order: i + 1 }));
}

export function AgendaSectionsPanel({
  sections,
  hasSlideDeck,
  slideDeckDoc,
  cohortCommitmentTrackingEnabled,
  onChange,
}: AgendaSectionsPanelProps) {
  const addSection = () => {
    onChange([...sections, emptySection(sections.length + 1)]);
  };

  const updateSection = (index: number, patch: Partial<SessionAgendaSection>) => {
    onChange(sections.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };

  const moveSection = (index: number, direction: 'up' | 'down') => {
    const swapWith = direction === 'up' ? index - 1 : index + 1;
    if (swapWith < 0 || swapWith >= sections.length) return;
    const next = sections.slice();
    [next[index], next[swapWith]] = [next[swapWith], next[index]];
    onChange(renumber(next));
  };

  const deleteSection = (index: number) => {
    onChange(renumber(sections.filter((_, i) => i !== index)));
  };

  return (
    <Card withBorder padding="lg" radius="md">
      <Group justify="space-between" align="center" mb="md">
        <Title order={3}>Agenda Sections</Title>
        <Button
          leftSection={<Plus size={16} />}
          onClick={addSection}
          variant="default"
          data-testid="agenda-add-section"
        >
          Add Section
        </Button>
      </Group>

      {sections.length === 0 ? (
        <EmptyState
          icon={<FileText size={32} />}
          title="No sections yet"
          description="Build the session's agenda by adding sections — give each a name, a duration, and (optionally) slides and facilitator prompts."
          action={
            <Button
              leftSection={<Plus size={16} />}
              onClick={addSection}
              data-testid="agenda-add-first-section"
            >
              Add your first section
            </Button>
          }
        />
      ) : (
        <Stack gap="md">
          {sections.map((section, i) => (
            <AgendaSectionCard
              key={section.id ?? `new-${i}-${section.sort_order}`}
              section={section}
              position={i + 1}
              total={sections.length}
              isFirst={i === 0}
              isLast={i === sections.length - 1}
              hasSlideDeck={hasSlideDeck}
              slideDeckDoc={slideDeckDoc}
              cohortCommitmentTrackingEnabled={cohortCommitmentTrackingEnabled}
              onChange={patch => updateSection(i, patch)}
              onMoveUp={() => moveSection(i, 'up')}
              onMoveDown={() => moveSection(i, 'down')}
              onDelete={() => deleteSection(i)}
            />
          ))}
          {/* MED-1073 (E1.3i / AC-AP1, AC-AP3) — duplicate Add Section at the
              bottom of the list so the CM can append without scrolling back to
              the top. Identical behavior to the top button. */}
          <Group justify="flex-end">
            <Button
              leftSection={<Plus size={16} />}
              onClick={addSection}
              variant="default"
              data-testid="agenda-add-section-bottom"
            >
              Add Section
            </Button>
          </Group>
        </Stack>
      )}
    </Card>
  );
}
